/**
 * ADA Fix: Main Navigation Menu Destroy Behavior
 * 
 * Issue: Executable submenus in main navigation do not properly destroy/close
 * when user presses ESC or tabs out of the menu (onBlur).
 * 
 * Recommendation:
 * - Allow ESC to destroy menu and return user to the executing control
 * - Allow menu to destroy onBlur when user tabs to next main nav element
 * 
 * This ensures proper keyboard navigation and doesn't trap users in submenus.
 */

(function() {
  'use strict';

  console.log('🎯 ADA Fix: Initializing main navigation menu destroy behavior...');

  /**
   * Find all focusable elements within a container
   */
  function getFocusableElements(container) {
    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ];

    return Array.from(container.querySelectorAll(focusableSelectors.join(', ')));
  }

  /**
   * Close a submenu and return focus to trigger
   */
  function closeSubmenu(trigger, submenu) {
    if (!submenu) return;

    // Hide the submenu
    submenu.setAttribute('hidden', '');
    
    // Update aria-expanded
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
      
      // Return focus to trigger
      trigger.focus();
    }

    console.log('✅ Submenu closed and focus returned to trigger');
  }

  /**
   * Close all open submenus
   */
  function closeAllSubmenus() {
    // Find all main menu triggers
    const triggers = document.querySelectorAll('[aria-controls^="MainMenu-"]');
    
    triggers.forEach(function(trigger) {
      const submenuId = trigger.getAttribute('aria-controls');
      if (!submenuId) return;

      const submenu = document.getElementById(submenuId);
      if (!submenu) return;

      // If submenu is visible (not hidden)
      if (!submenu.hasAttribute('hidden')) {
        closeSubmenu(trigger, submenu);
      }
    });
  }

  /**
   * Get the trigger element for a submenu
   */
  function getTriggerForSubmenu(submenuId) {
    return document.querySelector(`[aria-controls="${submenuId}"]`);
  }

  /**
   * Check if an element is inside a submenu
   */
  function isInsideSubmenu(element) {
    let current = element;
    while (current && current !== document.body) {
      if (current.classList && 
          (current.classList.contains('menu-dropdown') || 
           current.classList.contains('menu-mega'))) {
        return current;
      }
      current = current.parentElement;
    }
    return null;
  }

  /**
   * Setup keyboard handlers for submenu triggers
   */
  function setupSubmenuTriggerHandlers() {
    console.log('🔍 Setting up submenu trigger handlers...');

    const triggers = document.querySelectorAll('[aria-controls^="MainMenu-"]');
    
    console.log(`   Found ${triggers.length} submenu triggers`);

    triggers.forEach(function(trigger) {
      const submenuId = trigger.getAttribute('aria-controls');
      if (!submenuId) return;

      const submenu = document.getElementById(submenuId);
      if (!submenu) return;

      // Handle ESC key on trigger
      trigger.addEventListener('keydown', function(event) {
        const key = event.key;
        const isExpanded = trigger.getAttribute('aria-expanded') === 'true';

        // ESC: Close submenu if open
        if (key === 'Escape' || key === 'Esc') {
          if (isExpanded) {
            event.preventDefault();
            event.stopPropagation();
            closeSubmenu(trigger, submenu);
          }
        }
      });

      // Store trigger reference on submenu for later use
      submenu._trigger = trigger;
    });
  }

  /**
   * Setup keyboard handlers for submenu content
   */
  function setupSubmenuContentHandlers() {
    console.log('🔍 Setting up submenu content handlers...');

    const submenus = document.querySelectorAll('[id^="MainMenu-"]');
    
    console.log(`   Found ${submenus.length} submenus`);

    submenus.forEach(function(submenu) {
      const trigger = getTriggerForSubmenu(submenu.id);
      if (!trigger) return;

      // Handle ESC key within submenu
      submenu.addEventListener('keydown', function(event) {
        const key = event.key;

        // ESC: Close submenu and return to trigger
        if (key === 'Escape' || key === 'Esc') {
          event.preventDefault();
          event.stopPropagation();
          closeSubmenu(trigger, submenu);
        }
      });

      // Handle TAB to detect when user leaves the submenu
      submenu.addEventListener('keydown', function(event) {
        if (event.key !== 'Tab') return;

        const focusableElements = getFocusableElements(submenu);
        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        const isShiftTab = event.shiftKey;

        // If tabbing forward from last element
        if (!isShiftTab && document.activeElement === lastElement) {
          // Let the tab happen naturally, then close menu in a moment
          setTimeout(function() {
            // Check if focus has left the submenu
            if (!submenu.contains(document.activeElement)) {
              closeSubmenu(trigger, submenu);
            }
          }, 10);
        }
        
        // If tabbing backward from first element
        if (isShiftTab && document.activeElement === firstElement) {
          // Let the tab happen naturally, then close menu
          setTimeout(function() {
            if (!submenu.contains(document.activeElement)) {
              closeSubmenu(trigger, submenu);
            }
          }, 10);
        }
      });
    });
  }

  /**
   * Setup global focus monitoring for onBlur detection
   */
  function setupGlobalFocusMonitoring() {
    console.log('🔍 Setting up global focus monitoring...');

    // Monitor all focus changes
    document.addEventListener('focusin', function(event) {
      const focusedElement = event.target;

      // Find all open submenus
      const openSubmenus = document.querySelectorAll('[id^="MainMenu-"]:not([hidden])');

      openSubmenus.forEach(function(submenu) {
        const trigger = getTriggerForSubmenu(submenu.id);
        if (!trigger) return;

        // Check if focus is still inside the submenu or its trigger
        const isInsideSubmenuOrTrigger = 
          submenu.contains(focusedElement) || 
          trigger === focusedElement;

        // If focus has moved outside, close the submenu
        if (!isInsideSubmenuOrTrigger) {
          setTimeout(function() {
            closeSubmenu(trigger, submenu);
          }, 50);
        }
      });
    });
  }

  /**
   * Setup click outside detection
   */
  function setupClickOutsideDetection() {
    console.log('🔍 Setting up click outside detection...');

    document.addEventListener('click', function(event) {
      const target = event.target;

      // Check if click is inside any submenu or trigger
      const isInsideMenuSystem = 
        target.closest('[id^="MainMenu-"]') || 
        target.closest('[aria-controls^="MainMenu-"]') ||
        target.closest('.menu-dropdown') ||
        target.closest('.menu-mega');

      // If click is outside, close all submenus
      if (!isInsideMenuSystem) {
        closeAllSubmenus();
      }
    });
  }

  /**
   * Enhance ARIA attributes for better accessibility
   */
  function enhanceAriaAttributes() {
    console.log('🔍 Enhancing ARIA attributes...');

    // Add role="menu" to main menu list
    const mainMenuList = document.querySelector('.menu-list');
    if (mainMenuList) {
      // Keep role="list" for now, but ensure submenus have proper roles
    }

    // Add role="menuitem" to menu items
    const menuItems = document.querySelectorAll('.menu-list-item');
    menuItems.forEach(function(item) {
      if (item.tagName === 'A') {
        if (!item.hasAttribute('role')) {
          item.setAttribute('role', 'menuitem');
        }
      }
    });

    // Ensure submenus have role="menu"
    const submenus = document.querySelectorAll('[id^="MainMenu-"]');
    submenus.forEach(function(submenu) {
      if (submenu.tagName === 'UL' && !submenu.hasAttribute('role')) {
        submenu.setAttribute('role', 'menu');
      }
      
      if (submenu.classList.contains('menu-mega')) {
        submenu.setAttribute('role', 'region');
        submenu.setAttribute('aria-label', 'Submenu');
      }
    });

    // Ensure submenu items have role="menuitem"
    const submenuLinks = document.querySelectorAll('.menu-dropdown__link');
    submenuLinks.forEach(function(link) {
      if (!link.hasAttribute('role')) {
        link.setAttribute('role', 'menuitem');
      }
    });
  }

  /**
   * Setup MutationObserver for dynamic content
   */
  function setupDynamicContentObserver() {
    const observer = new MutationObserver(function(mutations) {
      let shouldReinitialize = false;

      mutations.forEach(function(mutation) {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(function(node) {
            if (node.nodeType === 1 && node.querySelector) {
              if (node.querySelector('.menu-list') || 
                  node.querySelector('[id^="MainMenu-"]')) {
                shouldReinitialize = true;
              }
            }
          });
        }

        // Watch for aria-expanded changes
        if (mutation.type === 'attributes' && mutation.attributeName === 'aria-expanded') {
          const target = mutation.target;
          const isExpanded = target.getAttribute('aria-expanded') === 'true';
          
          // If menu was just closed externally, ensure we update focus
          if (!isExpanded) {
            const submenuId = target.getAttribute('aria-controls');
            if (submenuId) {
              const submenu = document.getElementById(submenuId);
              if (submenu && !submenu.hasAttribute('hidden')) {
                submenu.setAttribute('hidden', '');
              }
            }
          }
        }
      });

      if (shouldReinitialize) {
        setTimeout(initialize, 100);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-expanded', 'hidden']
    });

    return observer;
  }

  /**
   * Initialize all menu destroy behaviors
   */
  function initialize() {
    console.log('🚀 Initializing menu destroy behaviors...');

    setupSubmenuTriggerHandlers();
    setupSubmenuContentHandlers();
    setupGlobalFocusMonitoring();
    setupClickOutsideDetection();
    enhanceAriaAttributes();
    setupDynamicContentObserver();

    console.log('✅ Menu destroy behaviors initialized');
  }

  // Run on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }

  // Also run after a delay for dynamic content
  setTimeout(initialize, 1000);

  // Expose functions for testing
  window.adaMenuDestroyFix = {
    closeSubmenu,
    closeAllSubmenus,
    getFocusableElements,
    initialize
  };

  console.log('🎯 ADA Menu Destroy Fix loaded. Functions available in window.adaMenuDestroyFix');

})();
